import React from "react";
import Image, { StaticImageData } from "next/image";
import Link from "next/link";
import logoImg from "@/images/logo_trans.png";
import logoLightImg from "@/images/logo-light.png";

export interface LogoProps {
  img?: StaticImageData;
  imgLight?: StaticImageData;
  className?: string;
}

// Displayed dimensions of the logo across the site. `max-h-16` (= 64px)
// drives the height; the width follows the natural ratio (350÷128 ≈ 2.73).
// We pass these explicit values to next/image so the optimizer emits a
// proper srcset (1× = 175 px / 2× = 350 px AVIF/WebP) instead of forcing
// every visitor to download the full 350×128 PNG (27 KB) Lighthouse
// flagged under "Improve image delivery" (~25 KB savings per logo).
const LOGO_W = 175;
const LOGO_H = 64;

/**
 * Site logo. Servi tel quel (`unoptimized`), volontairement.
 *
 * Historique : ce composant est passe a next/image pour eviter d'envoyer le
 * PNG de 27 Ko a tout le monde (Lighthouse "Improve image delivery", ~37 Ko
 * sur les deux copies). Puis, le 2026-09-03, le logo a disparu du site entier
 * en production.
 *
 * La cause : l'optimiseur d'images de Next met ses rendus en cache sur disque,
 * et `minimumCacheTTL` vaut 31 jours (next.config.js). UNE entree de ce cache
 * s'etait ecrite corrompue — `/_next/image?...logo_trans...&w=256&q=75`
 * renvoyait un PNG tronque de 500 octets la ou toutes les autres largeurs
 * rendaient un WebP valide (verifie : la meme largeur avec q=74 ou q=76,
 * c'est-a-dire une autre cle de cache, revenait parfaite). Et `sizes="175px"`
 * fait choisir precisement la variante 256 aux ecrans non-retina : le logo
 * etait donc invisible pour eux, et intact pour les autres — pendant 31 jours.
 *
 * D'ou ce choix : un logo de 27 Ko, immuable et deja en cache navigateur pour
 * un an (regle /_next/static de next.config.js), ne vaut pas de dependre d'un
 * cache serveur qui peut le faire disparaitre du site entier. Les ~18 Ko que
 * l'optimiseur economisait sont une fois par visiteur ; l'identite du site,
 * c'est chaque page.
 *
 * Si on veut recuperer ces kilo-octets un jour : convertir l'asset en WebP a
 * la main et l'importer, plutot que de repasser par l'optimiseur.
 *
 * `width` + `height` explicites (et pas d'utilitaires Tailwind de taille) :
 * sans eux, la boite s'ecrasait a vide, et le preload `priority` du LCP ne
 * partait plus.
 */
const Logo: React.FC<LogoProps> = ({
  img = logoImg,
  imgLight = logoLightImg,
  className = "",
}) => {
  return (
    <Link
      href="/"
      aria-label="Guydeez"
      className={`ttnc-logo inline-block text-primary-600 focus:outline-none focus:ring-0 md:w-48 ${className}`}
    >
      {/* Desktop / tablet logo */}
      {img && (
        <Image
          src={img}
          alt="Guydeez"
          width={LOGO_W}
          height={LOGO_H}
          priority
          unoptimized
          className="hidden md:block"
          style={{ height: "auto", width: `${LOGO_W}px` }}
        />
      )}

      {/* Mobile logo — same asset, slightly tighter margins */}
      <Image
        src={logoImg}
        alt="Guydeez"
        width={LOGO_W}
        height={LOGO_H}
        priority
        unoptimized
        className="block md:hidden mt-0 mr-3 -mb-3 ml-3"
        style={{ height: "auto", width: `${LOGO_W}px` }}
      />
    </Link>
  );
};

export default Logo;
